/**
 * Messaging hooks — TanStack Query for conversations and messages.
 * Adaptive polling: 3s in open chat, 30s conversations list, 60s badge.
 * Pauses polling when tab is hidden to save battery/traffic.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  fetchConversations,
  fetchMessages,
  sendMessage,
  markConversationRead,
  fetchTotalUnreadCount,
} from '@turnifly/shared/api'
import type { ConversationListItem, MessageItem } from '@turnifly/shared/api'
import { useAdaptiveInterval } from './use-notifications'

export const messagingKeys = {
  conversations: (userId: string) => ['messaging', 'conversations', userId] as const,
  messages: (conversationId: string) => ['messaging', 'messages', conversationId] as const,
  unreadCount: (userId: string) => ['messaging', 'unread', userId] as const,
} as const

// ── Conversations list (30s polling when visible) ──────────

export function useConversations(
  userId: string | undefined,
): ReturnType<typeof useQuery<ConversationListItem[]>> {
  const interval = useAdaptiveInterval(30_000)

  return useQuery({
    queryKey: messagingKeys.conversations(userId ?? ''),
    queryFn: () => fetchConversations(userId ?? ''),
    enabled: !!userId,
    staleTime: 15_000,
    refetchInterval: interval,
  })
}

// ── Messages in chat (3s polling when visible — feels real-time) ──

export function useMessages(
  conversationId: string | undefined,
  pollingInterval: number | false = 3_000,
): ReturnType<typeof useQuery<MessageItem[]>> {
  const interval = useAdaptiveInterval(
    typeof pollingInterval === 'number' ? pollingInterval : 0,
  )
  const effectiveInterval = pollingInterval === false ? false : interval

  return useQuery({
    queryKey: messagingKeys.messages(conversationId ?? ''),
    queryFn: () => fetchMessages(conversationId ?? ''),
    enabled: !!conversationId,
    staleTime: 2_000,
    refetchInterval: effectiveInterval,
  })
}

// ── Send message (optimistic update) ───────────────────────

interface SendMessageVars {
  senderId: string
  text: string | null
  mediaUrls?: string[]
}

export function useSendMessage(conversationId: string): ReturnType<typeof useMutation<MessageItem, Error, SendMessageVars>> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ senderId, text, mediaUrls }: SendMessageVars) =>
      sendMessage(conversationId, senderId, text, mediaUrls ?? []),
    onMutate: async ({ senderId, text, mediaUrls }) => {
      const key = messagingKeys.messages(conversationId)
      await queryClient.cancelQueries({ queryKey: key })
      const previous = queryClient.getQueryData<MessageItem[]>(key)

      const optimisticMessage: MessageItem = {
        id: `temp-${Date.now()}`,
        sender_id: senderId,
        text: text ?? null,
        media_urls: mediaUrls ?? [],
        is_read: false,
        created_at: new Date().toISOString(),
        edited_at: null,
      }

      queryClient.setQueryData<MessageItem[]>(key, (old) => [...(old ?? []), optimisticMessage])
      return { previous, key }
    },
    onError: (_err, _vars, context) => {
      if (context?.previous && context?.key) {
        queryClient.setQueryData(context.key, context.previous)
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: messagingKeys.messages(conversationId) })
      void queryClient.invalidateQueries({ queryKey: ['messaging', 'conversations'] })
    },
  })
}

// ── Mark conversation as read ──────────────────────────────

export function useMarkRead(conversationId: string, userId: string): ReturnType<typeof useMutation<void, Error, void>> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => markConversationRead(conversationId, userId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: messagingKeys.conversations(userId) })
      void queryClient.invalidateQueries({ queryKey: messagingKeys.unreadCount(userId) })
    },
  })
}

// ── Total unread count for badge (60s polling when visible) ──

export function useTotalUnreadCount(
  userId: string | undefined,
): ReturnType<typeof useQuery<number>> {
  const interval = useAdaptiveInterval(60_000)

  return useQuery({
    queryKey: messagingKeys.unreadCount(userId ?? ''),
    queryFn: () => fetchTotalUnreadCount(userId ?? ''),
    enabled: !!userId,
    staleTime: 30_000,
    refetchInterval: interval,
  })
}
